import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/UserService';
import { AssistantVoiceSettings } from '../components/AssistantVoiceSettings';
import { DeleteAccountSection } from '../components/DeleteAccountSection';
import { User as UserIcon, Mail, Award, Building, Shield, Save, X, Phone, Hash, Calendar, Briefcase, MapPin, AlertCircle } from 'lucide-react';

export const Profile: React.FC = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    credentials: user?.credentials || '',
    badgeNumber: user?.badgeNumber || '',
    department: user?.department || '',
    phone: user?.phone || '',
    employeeId: user?.employeeId || '',
    hireDate: user?.hireDate ? new Date(user.hireDate).toISOString().split('T')[0] : '',
    employmentType: user?.employmentType || '',
    addressStreet: user?.address?.street || '',
    addressCity: user?.address?.city || '',
    addressState: user?.address?.state || '',
    addressZip: user?.address?.zipCode || '',
    emergencyName: user?.emergencyContact?.name || '',
    emergencyRelationship: user?.emergencyContact?.relationship || '',
    emergencyPhone: user?.emergencyContact?.phone || '',
    trackMileage: user?.trackMileage || false,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        credentials: user.credentials || '',
        badgeNumber: user.badgeNumber || '',
        department: user.department || '',
        phone: user.phone || '',
        employeeId: user.employeeId || '',
        hireDate: user.hireDate ? new Date(user.hireDate).toISOString().split('T')[0] : '',
        employmentType: user.employmentType || '',
        addressStreet: user.address?.street || '',
        addressCity: user.address?.city || '',
        addressState: user.address?.state || '',
        addressZip: user.address?.zipCode || '',
        emergencyName: user.emergencyContact?.name || '',
        emergencyRelationship: user.emergencyContact?.relationship || '',
        emergencyPhone: user.emergencyContact?.phone || '',
        trackMileage: user.trackMileage || false,
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData({
      ...formData,
      [name]: finalValue,
    });
  };

  // Admin-provisioned fields (badge, department, employee ID, hire date,
  // employment type) are set up by admin/supervisor/HR; RBTs (and viewers)
  // can view but not edit them.
  const canEditHrFields =
    user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'hr';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await userService.updateUser(user.id, {
        // Personal fields — the user maintains these.
        name: formData.name,
        credentials: formData.credentials,
        phone: formData.phone,
        address: {
          street: formData.addressStreet,
          city: formData.addressCity,
          state: formData.addressState,
          zipCode: formData.addressZip,
        },
        emergencyContact: {
          name: formData.emergencyName,
          relationship: formData.emergencyRelationship,
          phone: formData.emergencyPhone,
        },
        trackMileage: formData.trackMileage,
        // Admin-provisioned HR/identity fields — only an admin may change these,
        // so they're omitted from the payload for everyone else (preserving the
        // existing values rather than overwriting them).
        ...(canEditHrFields
          ? {
              badgeNumber: formData.badgeNumber,
              department: formData.department,
              employeeId: formData.employeeId,
              hireDate: formData.hireDate ? new Date(formData.hireDate) : undefined,
              employmentType: formData.employmentType as 'W2' | '1099' | undefined,
            }
          : {}),
      });

      setSuccess('Profile updated successfully! Please refresh the page to see changes.');
      setEditing(false);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        credentials: user.credentials || '',
        badgeNumber: user.badgeNumber || '',
        department: user.department || '',
        phone: user.phone || '',
        employeeId: user.employeeId || '',
        hireDate: user.hireDate ? new Date(user.hireDate).toISOString().split('T')[0] : '',
        employmentType: user.employmentType || '',
        addressStreet: user.address?.street || '',
        addressCity: user.address?.city || '',
        addressState: user.address?.state || '',
        addressZip: user.address?.zipCode || '',
        emergencyName: user.emergencyContact?.name || '',
        emergencyRelationship: user.emergencyContact?.relationship || '',
        emergencyPhone: user.emergencyContact?.phone || '',
        trackMileage: user.trackMileage || false,
      });
    }
    setEditing(false);
    setError(null);
  };

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: 'var(--spacing-6)' }}>
            <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-2)' }}>
              My Profile
            </h1>
            <p style={{ color: 'var(--color-gray-600)' }}>
              Manage your personal information and settings
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div style={{
              marginBottom: 'var(--spacing-4)',
              padding: 'var(--spacing-4)',
              backgroundColor: 'var(--color-success-50)',
              border: '1px solid var(--color-success-200)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--color-success-800)',
            }}>
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: 'var(--spacing-4)',
              padding: 'var(--spacing-4)',
              backgroundColor: 'var(--color-error-50)',
              border: '1px solid var(--color-error-200)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--color-error-800)',
            }}>
              {error}
            </div>
          )}

          {/* Profile Card */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="card-title">Personal Information</h2>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="btn btn-secondary"
                >
                  Edit Profile
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                {/* Name */}
                <div>
                  <label htmlFor="name" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <UserIcon size={18} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!editing}
                    required
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/* Email (read-only) */}
                <div>
                  <label htmlFor="email" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <Mail size={18} />
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    disabled
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: 'var(--color-gray-50)',
                      cursor: 'not-allowed',
                    }}
                  />
                  <p style={{ marginTop: 'var(--spacing-1)', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
                    Email cannot be changed. Contact your administrator if you need to update it.
                  </p>
                </div>

                {/* Credentials (for RBTs) */}
                {user.role === 'rbt' && (
                  <div>
                    <label htmlFor="credentials" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                      <Award size={18} />
                      Credentials
                    </label>
                    <input
                      type="text"
                      id="credentials"
                      name="credentials"
                      value={formData.credentials}
                      onChange={handleChange}
                      disabled={!editing}
                      placeholder="e.g., RBT, BCBA"
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-gray-300)',
                        fontSize: 'var(--text-base)',
                        backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                      }}
                    />
                  </div>
                )}

                {/* Badge Number */}
                <div>
                  <label htmlFor="badgeNumber" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <Shield size={18} />
                    Badge Number
                    {!canEditHrFields && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-normal)' }}>
                        (set by admin)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    id="badgeNumber"
                    name="badgeNumber"
                    value={formData.badgeNumber}
                    onChange={handleChange}
                    disabled={!editing || !canEditHrFields}
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/* Department */}
                <div>
                  <label htmlFor="department" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <Building size={18} />
                    Department
                    {!canEditHrFields && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-normal)' }}>
                        (set by admin)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    disabled={!editing || !canEditHrFields}
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/* Role (read-only) */}
                <div>
                  <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    Role
                  </label>
                  <div style={{
                    padding: 'var(--spacing-3)',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--color-gray-50)',
                    border: '1px solid var(--color-gray-300)',
                  }}>
                    <span style={{ textTransform: 'capitalize', fontWeight: 'var(--font-medium)' }}>
                      {user.role}
                    </span>
                  </div>
                </div>

                {/* HR/Payroll Fields Section Header */}
                <div style={{ marginTop: 'var(--spacing-6)', paddingTop: 'var(--spacing-6)', borderTop: '1px solid var(--color-gray-200)' }}>
                  <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)' }}>
                    Employment Information
                  </h3>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <Phone size={18} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={!editing}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/* Employee ID (Admin/HR view only for security) */}
                {(user.role === 'admin' || user.role === 'hr') && (
                  <div>
                    <label htmlFor="employeeId" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                      <Hash size={18} />
                      Employee ID
                    </label>
                    <input
                      type="text"
                      id="employeeId"
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleChange}
                      disabled={!editing || !canEditHrFields}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-gray-300)',
                        fontSize: 'var(--text-base)',
                        backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                      }}
                    />
                  </div>
                )}

                {/* Hire Date (Admin/HR view only) */}
                {(user.role === 'admin' || user.role === 'hr') && (
                  <div>
                    <label htmlFor="hireDate" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                      <Calendar size={18} />
                      Hire Date
                    </label>
                    <input
                      type="date"
                      id="hireDate"
                      name="hireDate"
                      value={formData.hireDate}
                      onChange={handleChange}
                      disabled={!editing || !canEditHrFields}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-gray-300)',
                        fontSize: 'var(--text-base)',
                        backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                      }}
                    />
                  </div>
                )}

                {/* Employment Type (Admin/HR view only) */}
                {(user.role === 'admin' || user.role === 'hr') && (
                  <div>
                    <label htmlFor="employmentType" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                      <Briefcase size={18} />
                      Employment Type
                    </label>
                    <select
                      id="employmentType"
                      name="employmentType"
                      value={formData.employmentType}
                      onChange={handleChange}
                      disabled={!editing || !canEditHrFields}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-gray-300)',
                        fontSize: 'var(--text-base)',
                        backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                      }}
                    >
                      <option value="">Select type...</option>
                      <option value="W2">W2 Employee</option>
                      <option value="1099">1099 Contractor</option>
                    </select>
                  </div>
                )}

                {/* Address Section Header */}
                <div style={{ marginTop: 'var(--spacing-6)', paddingTop: 'var(--spacing-6)', borderTop: '1px solid var(--color-gray-200)' }}>
                  <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)' }}>
                    Address
                  </h3>
                </div>

                {/* Address Street */}
                <div>
                  <label htmlFor="addressStreet" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <MapPin size={18} />
                    Street Address
                  </label>
                  <input
                    type="text"
                    id="addressStreet"
                    name="addressStreet"
                    value={formData.addressStreet}
                    onChange={handleChange}
                    disabled={!editing}
                    placeholder="123 Main St"
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/* City, State, Zip */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--spacing-3)' }}>
                  <div>
                    <label htmlFor="addressCity" style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                      City
                    </label>
                    <input
                      type="text"
                      id="addressCity"
                      name="addressCity"
                      value={formData.addressCity}
                      onChange={handleChange}
                      disabled={!editing}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-gray-300)',
                        fontSize: 'var(--text-base)',
                        backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="addressState" style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                      State
                    </label>
                    <input
                      type="text"
                      id="addressState"
                      name="addressState"
                      value={formData.addressState}
                      onChange={handleChange}
                      disabled={!editing}
                      maxLength={2}
                      placeholder="OH"
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-gray-300)',
                        fontSize: 'var(--text-base)',
                        backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="addressZip" style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                      Zip
                    </label>
                    <input
                      type="text"
                      id="addressZip"
                      name="addressZip"
                      value={formData.addressZip}
                      onChange={handleChange}
                      disabled={!editing}
                      maxLength={5}
                      placeholder="12345"
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-gray-300)',
                        fontSize: 'var(--text-base)',
                        backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                      }}
                    />
                  </div>
                </div>

                {/* Emergency Contact Section Header */}
                <div style={{ marginTop: 'var(--spacing-6)', paddingTop: 'var(--spacing-6)', borderTop: '1px solid var(--color-gray-200)' }}>
                  <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)' }}>
                    Emergency Contact
                  </h3>
                </div>

                {/* Emergency Contact Name */}
                <div>
                  <label htmlFor="emergencyName" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <AlertCircle size={18} />
                    Contact Name
                  </label>
                  <input
                    type="text"
                    id="emergencyName"
                    name="emergencyName"
                    value={formData.emergencyName}
                    onChange={handleChange}
                    disabled={!editing}
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/* Emergency Contact Relationship */}
                <div>
                  <label htmlFor="emergencyRelationship" style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    Relationship
                  </label>
                  <input
                    type="text"
                    id="emergencyRelationship"
                    name="emergencyRelationship"
                    value={formData.emergencyRelationship}
                    onChange={handleChange}
                    disabled={!editing}
                    placeholder="Spouse, Parent, Sibling, etc."
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/* Emergency Contact Phone */}
                <div>
                  <label htmlFor="emergencyPhone" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                    <Phone size={18} />
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="emergencyPhone"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleChange}
                    disabled={!editing}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: 'var(--text-base)',
                      backgroundColor: editing ? 'white' : 'var(--color-gray-50)',
                    }}
                  />
                </div>

                {/*
                  Preferences section (Track Mileage for Reimbursement) is
                  hidden until automatic mileage tracking is wired end-to-end.
                  Nothing currently computes or persists mileage, so the toggle
                  promised a feature that didn't run. The trackMileage field,
                  MileageService, and timesheet/report fields remain in code so
                  this can be restored when the feature is built.
                */}
              </div>

              {/* Action Buttons */}
              {editing && (
                <div style={{ marginTop: 'var(--spacing-6)', display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-ghost"
                    disabled={loading}
                  >
                    <X size={18} style={{ marginRight: 'var(--spacing-2)' }} />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    <Save size={18} style={{ marginRight: 'var(--spacing-2)' }} />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </form>
          </div>

          <AssistantVoiceSettings />

          {/* Apple 5.1.1(v): every signed-in user can initiate account deletion. */}
          <DeleteAccountSection />
        </div>
    </div>
  );
};
